import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { DocumentDTO, ObligationDTO, TripDTO } from '@almanac/shared';
import { DocumentEditDrawer } from '../components/DocumentEditDrawer.js';
import { DocumentListItem } from '../components/DocumentListItem.js';
import { DocumentPreview } from '../components/DocumentPreview.js';
import { EditDrawer } from '../components/EditDrawer.js';
import { LoadStatus } from '../components/LoadStatus.js';
import { ObligationListItem } from '../components/ObligationListItem.js';
import { ObligationPreview } from '../components/ObligationPreview.js';
import { OccasionSuggestionBanner } from '../components/OccasionSuggestionBanner.js';
import { QuickAddBar } from '../components/QuickAddBar.js';
import { SectionTiles } from '../components/SectionTiles.js';
import {
  documentsTileSummary,
  expiryStatus,
  isExpiryHandled,
  isVaultRecord,
  useDocuments,
  vaultReviewDue,
  vaultRowDate,
  vaultTileSummary,
} from '../lib/documents.js';
import { budgetTileSummary, useBudgetMonths } from '../lib/budget.js';
import {
  splitTrips,
  travelTileSummary,
  tripDatesLabel,
  tripReadiness,
  tripWhenLabel,
  useTrips,
} from '../lib/travel.js';
import { useCurrentUser } from '../lib/me.js';
import { obligationSortKey, useLifeAreas, useObligations, useNowMinute } from '../lib/obligations.js';
import { usePeople } from '../lib/people.js';

const HORIZON_DAYS = 30;

export function TodayPage() {
  const obligations = useObligations();
  const documents = useDocuments();
  const people = usePeople();
  const lifeAreas = useLifeAreas();
  const nowMs = useNowMinute();
  const me = useCurrentUser();
  // Default to the panel-led layout until the preference loads, so the
  // screen never flashes the tiles-only view for a summary user.
  const layout = me.data?.homeLayout ?? 'summary';
  // Same split as the Obligations page: tap-row sets previewingId
  // (centered card), the card's Edit button sets editingId (drawer)
  // via close-then-open. See Obligations.tsx for the rationale.
  const [previewingId, setPreviewingId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  // Same close-then-open pair for the Documents card, so an expiring
  // document previews (and edits) in place — no detour into the
  // Documents tool just to glance at a renewal.
  const [docPreviewId, setDocPreviewId] = useState<string | null>(null);
  const [docEditingId, setDocEditingId] = useState<string | null>(null);

  const buckets = useMemo(() => bucketByHorizon(obligations.data ?? []), [obligations.data]);
  const now = useMemo(() => new Date(nowMs), [nowMs]);
  // Documents needing attention — expired or expiring soon, soonest
  // first. Empty on most days, and then the card simply isn't there.
  const expiringDocs = useMemo(
    () =>
      (documents.data ?? [])
        .filter((d) => {
          if (isExpiryHandled(d)) return false;
          const s = expiryStatus(d, now);
          return s === 'expired' || s === 'soon';
        })
        .sort((a, b) => (a.expiryDate ?? '').localeCompare(b.expiryDate ?? '')),
    [documents.data, now],
  );
  // Vault records whose review has lapsed — the Vault card below
  // (IDEAS "domain cards on Today": Vault review prompts).
  const vaultDue = useMemo(
    () =>
      (documents.data ?? [])
        .filter((d) => isVaultRecord(d) && vaultReviewDue(d, now))
        .sort((a, b) => a.title.localeCompare(b.title)),
    [documents.data, now],
  );
  const areasData = lifeAreas.data;
  const areaById = useMemo(() => {
    const map = new Map<string, NonNullable<typeof areasData>[number]>();
    for (const a of areasData ?? []) map.set(a.id, a);
    return map;
  }, [areasData]);

  const todayLabel = new Date().toLocaleDateString(undefined, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
  const previewing = obligations.data?.find((o) => o.id === previewingId) ?? null;
  const editing = obligations.data?.find((o) => o.id === editingId) ?? null;
  // Status line for the Obligations tile in the tiles-only layout —
  // leads with the most pressing bucket so the tile carries the same
  // signal the panel does, rather than a flat total.
  const obligationsSummary = obligationsTileSummary(buckets);
  const documentsSummary = useMemo(
    () => documentsTileSummary(documents.data, now),
    [documents.data, now],
  );
  const vaultSummary = vaultTileSummary(documents.data, now);
  const budgetMonths = useBudgetMonths();
  const budgetSummary = useMemo(
    () => budgetTileSummary(budgetMonths.data, now),
    [budgetMonths.data, now],
  );
  const trips = useTrips();
  const travelSummary = useMemo(() => travelTileSummary(trips.data, now), [trips.data, now]);
  // The next trip joins the card column once it's close enough to be
  // "the thing coming up" — about a quarter out — or already underway.
  const nextTrip = useMemo(() => {
    const next = splitTrips(trips.data ?? [], now).next;
    if (!next) return null;
    const start = new Date(`${next.startDate}T00:00:00`).getTime();
    const days = (start - now.getTime()) / (1000 * 60 * 60 * 24);
    return days <= 90 ? next : null;
  }, [trips.data, now]);
  const peopleCount = people.data?.length ?? 0;
  const peopleSummary =
    peopleCount > 0 ? `${peopleCount} ${peopleCount === 1 ? 'person' : 'people'}` : undefined;

  return (
    <section>
      <header className="today-header">
        <div>
          {layout === 'summary' && <div className="section__label">Today</div>}
          <h2 className="today-header__title">
            {greetingForNow()}.
          </h2>
          {layout === 'tiles' && <p className="today-header__date">{todayLabel}</p>}
        </div>
      </header>

      {/* The summary line only earns its place above the obligations
          panel; in the tiles layout the date carries the header. */}
      {layout === 'summary' && (
        <p className="today-intro">{summariseToday(buckets, expiringDocs.length, vaultDue.length)}</p>
      )}

      {layout === 'summary' ? (
        // On phones .today-columns is a plain block, so the order reads
        // exactly as before; ≥840px it becomes a two-column grid — the
        // day's flow left, the Jump-to rail right (styles.css).
        <div className="today-columns">
          <div className="today-main">
            <QuickAddBar />
            <OccasionSuggestionBanner onOpen={setPreviewingId} />
            <LoadStatus
              isLoading={obligations.isLoading}
              isError={obligations.isError}
              error={obligations.error}
            />
            <ObligationsCard
              buckets={buckets}
              areaById={areaById}
              onEdit={setPreviewingId}
            />
            {expiringDocs.length > 0 && (
              <DocumentsCard
                docs={expiringDocs}
                areaById={areaById}
                now={now}
                onOpen={setDocPreviewId}
              />
            )}
            {vaultDue.length > 0 && (
              <VaultCard docs={vaultDue} now={now} onOpen={setDocPreviewId} />
            )}
            {nextTrip && (
              <NextTripCard trip={nextTrip} obligations={obligations.data ?? []} now={now} />
            )}
          </div>
          <aside className="today-rail">
            <SectionTiles
              variant="small"
              obligationsSummary={obligationsSummary}
              documentsSummary={documentsSummary}
              peopleSummary={peopleSummary}
              vaultSummary={vaultSummary}
              budgetSummary={budgetSummary}
              travelSummary={travelSummary}
            />
          </aside>
        </div>
      ) : (
        <>
          <QuickAddBar />
          <OccasionSuggestionBanner onOpen={setPreviewingId} />
          <LoadStatus
            isLoading={obligations.isLoading}
            isError={obligations.isError}
            error={obligations.error}
          />
          <SectionTiles
            variant="big"
            obligationsSummary={obligationsSummary}
            documentsSummary={documentsSummary}
            peopleSummary={peopleSummary}
            vaultSummary={vaultSummary}
            budgetSummary={budgetSummary}
            travelSummary={travelSummary}
          />
        </>
      )}

      {previewing && (
        <ObligationPreview
          obligation={previewing}
          lifeArea={areaById.get(previewing.lifeAreaId)}
          lifeAreas={areasData ?? []}
          onClose={() => setPreviewingId(null)}
          onEdit={() => {
            const id = previewing.id;
            setPreviewingId(null);
            setEditingId(id);
          }}
        />
      )}

      <EditDrawer obligation={editing} onClose={() => setEditingId(null)} />

      {docPreviewId &&
        (() => {
          const doc = documents.data?.find((d) => d.id === docPreviewId);
          if (!doc) return null;
          return (
            <DocumentPreview
              document={doc}
              lifeAreaName={areaById.get(doc.lifeAreaId)?.name}
              onClose={() => setDocPreviewId(null)}
              onEdit={() => {
                setDocPreviewId(null);
                setDocEditingId(doc.id);
              }}
            />
          );
        })()}
      <DocumentEditDrawer
        target={
          docEditingId ? (documents.data?.find((d) => d.id === docEditingId) ?? null) : null
        }
        onClose={() => setDocEditingId(null)}
      />
    </section>
  );
}

/**
 * Today's Documents card — the second domain card, copying the
 * Obligations card's chassis. Lists documents that are expired or
 * expiring soon (the only Documents fact urgent enough for the front
 * page), soonest first; the per-row expiry label carries the urgency
 * in the accent colour. Rendered only when there's something to show —
 * on ordinary days the home screen stays quiet.
 */
const MAX_DOC_ROWS = 3;

function DocumentsCard({
  docs,
  areaById,
  now,
  onOpen,
}: {
  docs: DocumentDTO[];
  areaById: Map<string, { name: string }>;
  now: Date;
  onOpen: (id: string) => void;
}) {
  const visible = docs.slice(0, MAX_DOC_ROWS);
  return (
    <section className="today-card">
      <header className="today-card__header">
        <h3 className="today-card__title">Expiring documents</h3>
        <span className="today-card__count">{docs.length}</span>
      </header>

      <div className="list list--in-card" role="list">
        {visible.map((d) => (
          <DocumentListItem
            key={d.id}
            document={d}
            meta={[d.category, areaById.get(d.lifeAreaId)?.name]
              .filter(Boolean)
              .join(' · ')}
            now={now}
            onOpen={(doc) => onOpen(doc.id)}
          />
        ))}
      </div>

      <footer className="today-card__footer">
        <Link to="/documents?expiring=1" className="today-card__see-all">
          See all →
        </Link>
      </footer>
    </section>
  );
}

/**
 * Today's Vault card — review prompts only: the records whose annual
 * (or per-record) review has lapsed. Tapping a row opens the document
 * card in place, where "Still current" clears it from this list.
 * Rendered only while something is due — a well-kept vault keeps the
 * home screen quiet.
 */
function VaultCard({
  docs,
  now,
  onOpen,
}: {
  docs: DocumentDTO[];
  now: Date;
  onOpen: (id: string) => void;
}) {
  const visible = docs.slice(0, MAX_DOC_ROWS);
  return (
    <section className="today-card">
      <header className="today-card__header">
        <h3 className="today-card__title">Vault reviews</h3>
        <span className="today-card__count">{docs.length}</span>
      </header>

      <div className="list list--in-card" role="list">
        {visible.map((d) => (
          <DocumentListItem
            key={d.id}
            document={d}
            meta={d.category ?? ''}
            now={now}
            onOpen={(doc) => onOpen(doc.id)}
            dateOverride={vaultRowDate(d, now)}
          />
        ))}
      </div>

      <footer className="today-card__footer">
        <Link to="/vault" className="today-card__see-all">
          Review all →
        </Link>
      </footer>
    </section>
  );
}

/**
 * Today's Obligations card — a single bounded surface listing the
 * most pressing items from the next-30-day horizon as one urgency-
 * ranked list. Establishes the dashboard pattern that Documents /
 * Vault / Inventory cards will copy when those modules land.
 *
 * Visually distinct from the Obligations tab in three ways:
 *  1. Card chrome (surface, border, rounded corners, footer link).
 *  2. No time-horizon sub-headings inside the card — the per-row
 *     date label (`today`, `in 4d`, `2d overdue`) already carries
 *     that signal, so a redundant `Today / This week / Next 30`
 *     hierarchy was pure chrome that made the card taller and
 *     created a left-edge alignment tension with the row content.
 *  3. Rows hide the life-area badge (the card header conveys the
 *     domain). Occasion rows keep their "Birthday · turns N" meta
 *     since that's essential context, not redundant labelling.
 *
 * Display is capped at MAX_VISIBLE rows — the count badge in the
 * header shows the total, and `See all →` carries the rest into the
 * Obligations tab. Keeps the card short on busy weeks without
 * hiding the magnitude of what's coming.
 */
const MAX_VISIBLE = 5;

function ObligationsCard({
  buckets,
  areaById,
  onEdit,
}: {
  buckets: Buckets;
  areaById: Map<string, { id: string; name: string; slug: string; icon: string | null; sortOrder: number; visibility: string }>;
  onEdit: (id: string) => void;
}) {
  // Flatten the four buckets back into one list, then sort by
  // effective due date. Overdue items (past dates) naturally lead,
  // followed by today, tomorrow, this week, next 30 — the same
  // ranking the buckets used to express, but now applied as row
  // order rather than as headings.
  const all = [
    ...buckets.overdue.tasks,
    ...buckets.overdue.occasions,
    ...buckets.today.tasks,
    ...buckets.today.occasions,
    ...buckets.thisWeek.tasks,
    ...buckets.thisWeek.occasions,
    ...buckets.next30.tasks,
    ...buckets.next30.occasions,
  ].sort((a, b) => obligationSortKey(a).localeCompare(obligationSortKey(b)));
  const visible = all.slice(0, MAX_VISIBLE);

  return (
    <section className="today-card">
      <header className="today-card__header">
        <h3 className="today-card__title">Obligations</h3>
        {all.length > 0 && <span className="today-card__count">{all.length}</span>}
      </header>

      {all.length === 0 ? (
        <p className="today-card__empty">Nothing pressing in the next month.</p>
      ) : (
        <div className="list list--in-card" role="list">
          {visible.map((o) => (
            <ObligationListItem
              key={o.id}
              obligation={o}
              lifeArea={areaById.get(o.lifeAreaId) as never}
              onEdit={onEdit}
              hideLifeArea
            />
          ))}
        </div>
      )}

      <footer className="today-card__footer">
        <Link to="/obligations" className="today-card__see-all">
          See all →
        </Link>
      </footer>
    </section>
  );
}

interface BucketSlice {
  tasks: ObligationDTO[];
  occasions: ObligationDTO[];
}

interface Buckets {
  overdue: BucketSlice;
  today: BucketSlice;
  thisWeek: BucketSlice;
  next30: BucketSlice;
}

function bucketByHorizon(rows: ObligationDTO[]): Buckets {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const empty = (): BucketSlice => ({ tasks: [], occasions: [] });
  const out: Buckets = {
    overdue: empty(),
    today: empty(),
    thisWeek: empty(),
    next30: empty(),
  };

  function place(slice: BucketSlice, o: ObligationDTO) {
    if (o.kind === 'occasion') slice.occasions.push(o);
    else slice.tasks.push(o);
  }

  for (const o of rows) {
    if (o.status === 'completed') continue;
    const due = o.nextDueDate ?? o.dueDate;
    if (!due) continue;
    const target = new Date(`${due.slice(0, 10)}T00:00:00`);
    if (Number.isNaN(target.getTime())) continue;
    const diff = Math.round((target.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    if (diff < 0) place(out.overdue, o);
    else if (diff === 0) place(out.today, o);
    else if (diff <= 7) place(out.thisWeek, o);
    else if (diff <= HORIZON_DAYS) place(out.next30, o);
  }

  const byDate = (a: ObligationDTO, b: ObligationDTO) =>
    obligationSortKey(a).localeCompare(obligationSortKey(b));
  for (const slice of [out.overdue, out.today, out.thisWeek, out.next30]) {
    slice.tasks.sort(byDate);
    slice.occasions.sort(byDate);
  }
  return out;
}

function summariseToday(b: Buckets, expiringDocs: number, vaultDue: number): string {
  const counts = {
    overdue: b.overdue.tasks.length + b.overdue.occasions.length,
    today: b.today.tasks.length + b.today.occasions.length,
    thisWeek: b.thisWeek.tasks.length + b.thisWeek.occasions.length,
    next30: b.next30.tasks.length + b.next30.occasions.length,
  };
  // The trailing "Plus …" sentence folds in whatever the domain cards
  // below carry: expiring documents, lapsed vault reviews, or both.
  const extras = [
    expiringDocs > 0
      ? `${expiringDocs} ${expiringDocs === 1 ? 'document' : 'documents'} expiring`
      : null,
    vaultDue > 0
      ? `${vaultDue} vault ${vaultDue === 1 ? 'review' : 'reviews'} due`
      : null,
  ].filter(Boolean);
  const docsSuffix = extras.length > 0 ? ` Plus ${extras.join(' and ')}.` : '';
  const total = counts.overdue + counts.today + counts.thisWeek + counts.next30;
  if (total === 0) {
    return extras.length > 0
      ? `Nothing pressing in the next month.${docsSuffix}`
      : 'Nothing pressing in the next month. The kettle awaits.';
  }
  // Surface birthdays separately when there are any inside the
  // 30-day horizon — they feel different from a generic "obligation"
  // and the summary should respect that. Plain task counts retain
  // their existing voice.
  const occasionsAhead =
    b.overdue.occasions.length +
    b.today.occasions.length +
    b.thisWeek.occasions.length +
    b.next30.occasions.length;
  const parts: string[] = [];
  if (counts.overdue) parts.push(`${counts.overdue} overdue`);
  if (counts.today) parts.push(`${counts.today} today`);
  if (counts.thisWeek) parts.push(`${counts.thisWeek} this week`);
  if (counts.next30) parts.push(`${counts.next30} in the next month`);
  const suffix =
    occasionsAhead > 0
      ? ` Includes ${occasionsAhead} ${occasionsAhead === 1 ? 'birthday or anniversary' : 'birthdays and anniversaries'}.`
      : '';
  return parts.join(' · ') + '.' + suffix + docsSuffix;
}

/**
 * Compact status line for the Obligations tile (tiles layout). Leads
 * with the most pressing horizon so the tile is as informative as the
 * panel — overdue first, then today, then the wider horizon — and
 * reads gracefully when there's nothing due.
 */
function obligationsTileSummary(b: Buckets): string {
  const size = (s: BucketSlice) => s.tasks.length + s.occasions.length;
  const overdue = size(b.overdue);
  const today = size(b.today);
  const week = size(b.thisWeek);
  const month = size(b.next30);
  if (overdue > 0) return `${overdue} overdue`;
  if (today > 0) return today === 1 ? '1 due today' : `${today} due today`;
  if (week > 0) return `${week} this week`;
  if (month > 0) return `${month} this month`;
  return 'Nothing due';
}

function greetingForNow(): string {
  const h = new Date().getHours();
  if (h < 5) return 'Late evening';
  if (h < 12) return 'Good morning';
  if (h < 18) return 'Good afternoon';
  return 'Good evening';
}

/**
 * Today's Next-trip card — the Travel domain card (IDEAS "domain cards
 * on Today"). Shown only when a trip is underway or within ~3 months;
 * ordinary weeks stay quiet. One glance: when, how ready, and the way
 * into the container.
 */
function NextTripCard({
  trip,
  obligations,
  now,
}: {
  trip: TripDTO;
  obligations: ObligationDTO[];
  now: Date;
}) {
  const readiness = tripReadiness(trip, obligations);
  const openTasks = obligations.filter(
    (o) => o.tripId === trip.id && o.status !== 'completed',
  ).length;
  const packed = trip.packing.filter((p) => p.done).length;
  const bits = [
    tripDatesLabel(trip).split(' – ')[0],
    openTasks > 0 ? `${openTasks} ${openTasks === 1 ? 'task' : 'tasks'} open` : null,
    trip.packing.length > 0 ? `${packed}/${trip.packing.length} packed` : null,
  ].filter(Boolean);
  return (
    <section className="today-card">
      <header className="today-card__header">
        <h3 className="today-card__title">Next trip</h3>
        <span className="today-card__count">{tripWhenLabel(trip, now)}</span>
      </header>
      <div className="trip-next">
        <div className="trip-next__title">{trip.title}</div>
        <div className="trip-next__sub">{bits.join(' · ')}</div>
        {readiness && (
          <div className="trip-ready trip-ready--card" aria-hidden>
            <div className="trip-ready__track">
              <div
                className="trip-ready__fill"
                style={{ width: `${Math.round((readiness.done / readiness.total) * 100)}%` }}
              />
            </div>
          </div>
        )}
      </div>
      <footer className="today-card__footer">
        <Link to={`/travel/${trip.id}`} className="today-card__see-all">
          Open the trip →
        </Link>
      </footer>
    </section>
  );
}
